/* eslint-disable new-cap */
/**
 * REST API を使用したマッチング検索 hook
 * OpenAPI仕様から生成された型を使用（SSOT）
 */
import { useCallback, useState } from "react";
import { api } from "../api/client";
import type { components } from "../api/schema";

// =====================================
// 生成された型を再エクスポート
// =====================================

export type EmailMatch = components["schemas"]["EmailMatch"];
export type MatchingResponse = components["schemas"]["MatchingResponse"];

// =====================================
// REST API Hook Types
// =====================================

export interface UseMatchingRestApiResult {
  findMatching: (candidateInfo: string) => Promise<MatchingResponse>;
  loading: boolean;
  error: Error | undefined;
  data: MatchingResponse | undefined;
}

/**
 * マッチング検索を REST API で実行する hook
 * @return {UseMatchingRestApiResult} マッチング実行関数、ローディング状態、エラー、データを含むオブジェクト
 */
export function useMatchingRestApi(): UseMatchingRestApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<MatchingResponse | undefined>(undefined);

  const findMatching = useCallback(
    async (candidateInfo: string): Promise<MatchingResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const { data: responseData, error: apiError } = await api.POST(
          "/api/matching",
          {
            body: { candidateInfo },
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    findMatching,
    loading,
    error,
    data,
  };
}

export default useMatchingRestApi;
